import 'leader-line';
import { Component, ViewChild, ElementRef, OnInit, signal, OnChanges, Input, EventEmitter, Output, effect, computed } from '@angular/core';
import { moveItemInArray } from '@angular/cdk/drag-drop';
import { FlowObjectType, FlowObjectDefinition, FlowObjectTypeDescription, FlowObjectTypePublicName } from '../../../models/flow-object.model';
import { Grouping } from '../../../models/grouping.model';
import { GroupingColumn } from '../../../models/grouping-column.model';
import { ConfigSchema } from '../../../models/config-schema.model';
import { IMPORTS } from './_imports';
import { Utils } from '../../../shared/utils';
import { SCSS_VARS } from '../../../shared/shared-scss-ts-variables';
import { Enums } from '../../../shared/enums';
import { OnAfterSelectFlowObjectDto } from '../../../shared/helper-classes';

declare var LeaderLine: any;

@Component({
    selector: 'canvas-body',
    templateUrl: './canvas-body.component.html',
    styleUrl: './canvas-body.component.scss',
    standalone: true,
    imports: IMPORTS
})
export class CanvasBodyComponent implements OnInit, OnChanges {
    // #region [ViewChild]
    @ViewChild('mainPathRef') mainPathRef: ElementRef;
    @ViewChild('firstGatewayPathRef') firstGatewayPathRef: ElementRef;
    @ViewChild('secondGatewayPathRef') secondGatewayPathRef: ElementRef;
    // #endregion

    // #region [props]
    mainPathFlowObjectRefs: ElementRef[] = [];
    firstGatewayPathFlowObjectRefs: ElementRef[] = [];
    secondGatewayPathFlowObjectRefs: ElementRef[] = [];
    // #endregion

    // #region [signals]
    $isBootstrapFinished = signal(false);
    $isReadOnlyMode = signal(false);
    $selectedFlowObjects = signal([] as FlowObjectDefinition[]);
    $selectedGroupings = signal([] as Grouping[]);
    $selectedGroupingColumns = signal([] as GroupingColumn[]);
    $selectedVersion = signal(null as number);
    $clearLinksTrigger = signal(false);
    $linkFlowObjectsTrigger = signal(false);
    $reorderFlowObjectsTrigger = signal(false);
    $onAfterSelectFlowObjectTrigger = signal(null as OnAfterSelectFlowObjectDto);
    // #endregion

    // #region [computeds]
    $hasGatewayPath = computed(() => this.$selectedFlowObjects().some(x => x.typeId == FlowObjectType.GatewayPaths));
    $mainPathGroupings = computed(() => this.$selectedGroupings().filter(x => !x.isFirstGatewayPath && !x.isSecondGatewayPath));
    $mainPathGroupingColumns = computed(() => this.$selectedGroupingColumns().filter(x => !x.isFirstGatewayPath && !x.isSecondGatewayPath));
    $mainPathFlowObjects = computed(() => this.$selectedFlowObjects().filter(x => !x.isFirstGatewayPath && !x.isSecondGatewayPath));
    $firstGatewayPathGroupings = computed(() => this.$selectedGroupings().filter(x => x.isFirstGatewayPath));
    $firstGatewayPathGroupingColumns = computed(() => this.$selectedGroupingColumns().filter(x => x.isFirstGatewayPath));
    $firstGatewayPathFlowObjects = computed(() => this.$selectedFlowObjects().filter(x => x.isFirstGatewayPath));
    $firstGatewayPathName = computed(() => {
        if (!this.$hasGatewayPath()) return null;
        let configSchema = JSON.parse(this.$selectedFlowObjects().find(x => x.typeId == FlowObjectType.GatewayPaths).configSchema) as ConfigSchema;
        return configSchema.taskGatewayPaths.firstGatewayPathName;
    });
    $secondGatewayPathGroupings = computed(() => this.$selectedGroupings().filter(x => x.isSecondGatewayPath));
    $secondGatewayPathGroupingColumns = computed(() => this.$selectedGroupingColumns().filter(x => x.isSecondGatewayPath));
    $secondGatewayPathFlowObjects = computed(() => this.$selectedFlowObjects().filter(x => x.isSecondGatewayPath));
    $secondGatewayPathName = computed(() => {
        if (!this.$hasGatewayPath()) return null;
        let configSchema = JSON.parse(this.$selectedFlowObjects().find(x => x.typeId == FlowObjectType.GatewayPaths).configSchema) as ConfigSchema;
        return configSchema.taskGatewayPaths.secondGatewayPathName;
    });
    // #endregion

    // #region [effects]
    $clearLinksTriggerEvent = effect(() => {
        if (this.$clearLinksTrigger()) {
            this.clearLinks();
            this.$clearLinksTrigger.set(false);
            this._clearLinksTriggerResetEvent.emit();
        }
    }, { allowSignalWrites: true });
    $linkFlowObjectsTriggerEvent = effect(() => {
        if (this.$linkFlowObjectsTrigger()) {
            this.linkFlowObjects();
            this.$linkFlowObjectsTrigger.set(false);
            this._linkFlowObjectsTriggerResetEvent.emit();
        }
    }, { allowSignalWrites: true });
    $reorderFlowObjectsTriggerEvent = effect(() => {
        if (this.$reorderFlowObjectsTrigger()) {
            this.reorderFlowObjects();
            this.$reorderFlowObjectsTrigger.set(false);
            this._reorderFlowObjectsTriggerResetEvent.emit();
        }
    }, { allowSignalWrites: true });
    $onAfterSelectFlowObjectTriggerEvent = effect(() => {
        if (this.$onAfterSelectFlowObjectTrigger() != null) {
            this.onAfterSelectFlowObject(this.$onAfterSelectFlowObjectTrigger());
            this.$onAfterSelectFlowObjectTrigger.set(null);
            this._onAfterSelectFlowObjectTriggerResetEvent.emit();
        }
    }, { allowSignalWrites: true });
    // #endregion

    // #region [Input/Output]
    @Input() _isBootstrapFinished: boolean;
    @Input() _isReadOnlyMode: boolean;
    @Input() _selectedFlowObjects: FlowObjectDefinition[];
    @Input() _selectedGroupings: Grouping[];
    @Input() _selectedGroupingColumns: GroupingColumn[];
    @Input() _selectedVersion: number;
    @Input() _clearLinksTrigger: boolean;
    @Input() _linkFlowObjectsTrigger: boolean;
    @Input() _reorderFlowObjectsTrigger: boolean;
    @Input() _onAfterSelectFlowObjectTrigger: OnAfterSelectFlowObjectDto;
    @Output() _selectedFlowObjectsChangeEvent = new EventEmitter<FlowObjectDefinition[]>();
    @Output() _selectedGroupingsChangeEvent = new EventEmitter<Grouping[]>();
    @Output() _selectedGroupingColumnsChangeEvent = new EventEmitter<GroupingColumn[]>();
    @Output() _toggleEditAreaEvent = new EventEmitter<FlowObjectDefinition>();
    @Output() _clearLinksTriggerResetEvent = new EventEmitter<any>();
    @Output() _linkFlowObjectsTriggerResetEvent = new EventEmitter<any>();
    @Output() _reorderFlowObjectsTriggerResetEvent = new EventEmitter<any>();
    @Output() _onAfterSelectFlowObjectTriggerResetEvent = new EventEmitter<any>();
    // #endregion

    constructor( ) { }

    ngOnInit() { }

    ngOnChanges() {
        this.$isBootstrapFinished.set(this._isBootstrapFinished);
        this.$isReadOnlyMode.set(this._isReadOnlyMode);
        this.$selectedFlowObjects.set(this._selectedFlowObjects);
        this.$selectedGroupings.set(this._selectedGroupings);
        this.$selectedGroupingColumns.set(this._selectedGroupingColumns);
        this.$selectedVersion.set(this._selectedVersion);
        this.$clearLinksTrigger.set(this._clearLinksTrigger);
        this.$linkFlowObjectsTrigger.set(this._linkFlowObjectsTrigger);
        this.$reorderFlowObjectsTrigger.set(this._reorderFlowObjectsTrigger);
        this.$onAfterSelectFlowObjectTrigger.set(this._onAfterSelectFlowObjectTrigger);
    }

    gatherMainPathFlowObjectRefs(event) {
        this.mainPathFlowObjectRefs = this.mainPathFlowObjectRefs.concat(event);
    }

    gatherFirstGatewayPathFlowObjectRefs(event) {
        this.firstGatewayPathFlowObjectRefs = this.firstGatewayPathFlowObjectRefs.concat(event);
    }

    gatherSecondGatewayPathFlowObjectRefs(event) {
        this.secondGatewayPathFlowObjectRefs = this.secondGatewayPathFlowObjectRefs.concat(event);
    }

    moveGroupingBackwards(event: MouseEvent, flowObject: FlowObjectDefinition) {
        if (this.$isReadOnlyMode()) {
            event?.stopPropagation();
            return;
        }

        let fromIndex = flowObject.groupingColumn.grouping.outerIndex;
        if (
            fromIndex == 0
            || fromIndex == this.$mainPathGroupings().length
            || fromIndex == this.$mainPathGroupings().length + this.$firstGatewayPathGroupings().length
        ) {
            event?.stopPropagation();
            return;
        }

        Utils.updSignal(this.$selectedGroupings, x => {
            x[fromIndex].outerIndex -= 1;
            x[fromIndex - 1].outerIndex += 1;
            return x;
        });

        let clone = structuredClone(this.$selectedGroupings());
        moveItemInArray(clone, fromIndex, fromIndex - 1);
        Utils.setSignal(this.$selectedGroupings, clone);
        this._selectedGroupingsChangeEvent.emit(this.$selectedGroupings());

        this.reorderFlowObjects();
        this.linkFlowObjects();
        event?.stopPropagation();
    }

    moveGroupingForwards(event: MouseEvent, flowObject: FlowObjectDefinition) {
        if (this.$isReadOnlyMode()) {
            event.stopPropagation();
            return;
        }

        let fromIndex = flowObject.groupingColumn.grouping.outerIndex;
        if (
            fromIndex == this.$mainPathGroupings().length - 1
            || fromIndex == this.$mainPathGroupings().length + this.$firstGatewayPathGroupings().length - 1
            || fromIndex == this.$selectedGroupings().length - 1
            || this.$selectedGroupings().find(x => {
                if (x?.groupingColumns == null) return;
                x.groupingColumns.some(y => {
                    if (y?.flowObjectDefinitions == null) return;
                    y.flowObjectDefinitions.some(z =>
                        z?.typeId == FlowObjectType.GatewayPaths
                    );
                });
            })?.outerIndex == fromIndex + 1
        ) {
            event.stopPropagation();
            return;
        }

        Utils.updSignal(this.$selectedGroupings, x => {
            x[fromIndex].outerIndex += 1;
            x[fromIndex + 1].outerIndex -= 1;
            return x;
        });

        let clone = structuredClone(this.$selectedGroupings());
        moveItemInArray(clone, fromIndex, fromIndex + 1);
        Utils.setSignal(this.$selectedGroupings, clone);
        this._selectedGroupingsChangeEvent.emit(this.$selectedGroupings());

        this.reorderFlowObjects();
        this.linkFlowObjects();
        event.stopPropagation();
    }

    moveGroupingUpwards(event: MouseEvent, flowObject: FlowObjectDefinition) {
        if (this.$isReadOnlyMode()) {
            event.stopPropagation();
            return;
        }

        let toIndex: number;
        let groupingColumnId = flowObject.groupingColumnId;
        let groupingId = this.$selectedGroupingColumns().find(x => x.id == flowObject.groupingColumnId).groupingId;

        if (flowObject.isFirstGatewayPath) {
            Utils.updSignal(this.$selectedGroupings, x => {
                let grouping = x.find(y => y.id == groupingId);
                grouping.isFirstGatewayPath = false;
                grouping.isSecondGatewayPath = false;
                return x;
            });

            Utils.updSignal(this.$selectedGroupingColumns, x => {
                x.filter(y => y.groupingId == groupingId).forEach(z => {
                    z.isFirstGatewayPath = false;
                    z.isSecondGatewayPath = false;
                });
                return x;
            });

            Utils.updSignal(this.$selectedFlowObjects, x => {
                x.filter(y => y.groupingColumnId == groupingColumnId).forEach(z => {
                    z.isFirstGatewayPath = false;
                    z.isSecondGatewayPath = false;
                });
                return x;
            });

            toIndex = this.$mainPathGroupings().length - 2 >= 0 ? this.$mainPathGroupings().length - 2 : 0;

            this.firstGatewayPathFlowObjectRefs = this.firstGatewayPathFlowObjectRefs.filter(x => x.nativeElement.dataset.id != flowObject.id);
        } else if (flowObject.isSecondGatewayPath) {
            Utils.updSignal(this.$selectedGroupings, x => {
                let grouping = x.find(y => y.id == groupingId);
                grouping.isFirstGatewayPath = true;
                grouping.isSecondGatewayPath = false;
                return x;
            });

            Utils.updSignal(this.$selectedGroupingColumns, x => {
                x.filter(y => y.groupingId == groupingId).forEach(z => {
                    z.isFirstGatewayPath = true;
                    z.isSecondGatewayPath = false;
                });
                return x;
            });

            Utils.updSignal(this.$selectedFlowObjects, x => {
                x.filter(y => y.groupingColumnId == groupingColumnId).forEach(z => {
                    z.isFirstGatewayPath = true;
                    z.isSecondGatewayPath = false;
                });
                return x;
            });

            toIndex = this.$mainPathGroupings().length + this.$firstGatewayPathGroupings().length - 1;

            this.secondGatewayPathFlowObjectRefs = this.secondGatewayPathFlowObjectRefs.filter(x => x.nativeElement.dataset.id != flowObject.id);
        }

        while (this.$selectedGroupings().find(x => x.id == groupingId).outerIndex > toIndex) {
            let fromIndex = this.$selectedGroupings().find(x => x.id == groupingId).outerIndex;

            Utils.updSignal(this.$selectedGroupings, x => {
                x[fromIndex].outerIndex -= 1;
                x[fromIndex - 1].outerIndex += 1;
                return x;
            });

            let clone = structuredClone(this.$selectedGroupings());
            moveItemInArray(clone, fromIndex, fromIndex - 1);
            Utils.setSignal(this.$selectedGroupings, clone);
            this._selectedGroupingsChangeEvent.emit(this.$selectedGroupings());
        }

        this.reorderFlowObjects();
        this.linkFlowObjects();
        event.stopPropagation();
    }

    moveGroupingDownwards(event: MouseEvent, flowObject: FlowObjectDefinition) {
        if (this.$isReadOnlyMode()) {
            event.stopPropagation();
            return;
        }

        let toIndex: number;
        let groupingColumnId = flowObject.groupingColumnId;
        let groupingId = this.$selectedGroupingColumns().find(x => x.id == flowObject.groupingColumnId).groupingId;

        if (!flowObject.isFirstGatewayPath && !flowObject.isSecondGatewayPath) {
            Utils.updSignal(this.$selectedGroupings, x => {
                let grouping = x.find(y => y.id == groupingId);
                grouping.isFirstGatewayPath = true;
                grouping.isSecondGatewayPath = false;
                return x;
            });

            Utils.updSignal(this.$selectedGroupingColumns, x => {
                x.filter(y => y.groupingId == groupingId).forEach(z => {
                    z.isFirstGatewayPath = true;
                    z.isSecondGatewayPath = false;
                });
                return x;
            });

            Utils.updSignal(this.$selectedFlowObjects, x => {
                x.filter(y => y.groupingColumnId == groupingColumnId).forEach(z => {
                    z.isFirstGatewayPath = true;
                    z.isSecondGatewayPath = false;
                });
                return x;
            });

            toIndex = this.$mainPathGroupings().length + this.$firstGatewayPathGroupings().length - 1;

            this.mainPathFlowObjectRefs = this.mainPathFlowObjectRefs.filter(x => x.nativeElement.dataset.id != flowObject.id);
        } else if (flowObject.isFirstGatewayPath) {
            Utils.updSignal(this.$selectedGroupings, x => {
                let grouping = x.find(y => y.id == groupingId);
                grouping.isFirstGatewayPath = false;
                grouping.isSecondGatewayPath = true;
                return x;
            });

            Utils.updSignal(this.$selectedGroupingColumns, x => {
                x.filter(y => y.groupingId == groupingId).forEach(z => {
                    z.isFirstGatewayPath = false;
                    z.isSecondGatewayPath = true;
                });
                return x;
            });

            Utils.updSignal(this.$selectedFlowObjects, x => {
                x.filter(y => y.groupingColumnId == groupingColumnId).forEach(z => {
                    z.isFirstGatewayPath = false;
                    z.isSecondGatewayPath = true;
                });
                return x;
            });

            toIndex = this.$mainPathGroupings().length + this.$firstGatewayPathGroupings().length + this.$secondGatewayPathGroupings().length - 1;

            this.firstGatewayPathFlowObjectRefs = this.firstGatewayPathFlowObjectRefs.filter(x => x.nativeElement.dataset.id != flowObject.id);
        }

        while (this.$selectedGroupings().find(x => x.id == groupingId).outerIndex < toIndex) {
            let fromIndex = this.$selectedGroupings().find(x => x.id == groupingId).outerIndex;

            Utils.updSignal(this.$selectedGroupings, x => {
                x[fromIndex].outerIndex += 1;
                x[fromIndex + 1].outerIndex -= 1;
                return x;
            });

            let clone = structuredClone(this.$selectedGroupings());
            moveItemInArray(clone, fromIndex, fromIndex + 1);
            Utils.setSignal(this.$selectedGroupings, clone);
            this._selectedGroupingsChangeEvent.emit(this.$selectedGroupings());
        }

        this.reorderFlowObjects();
        this.linkFlowObjects();
        event.stopPropagation();
    }

    removeGrouping(event: MouseEvent, flowObject: FlowObjectDefinition) {
        if (this.$isReadOnlyMode()) {
            event.stopPropagation();
            return;
        }

        let flowObjectsToRemove: FlowObjectDefinition[] = [];
        let groupingIndexToRemove = flowObject.groupingColumn.grouping.outerIndex;
        let groupingColumnIndexesToRemove: number[] = [];
        let flowObjectIndexesToRemove: number[] = [];

        let groupingColumnsToRemove = this.$selectedGroupingColumns().filter(x => x.groupingId == flowObject.groupingColumn.groupingId);
        groupingColumnsToRemove.forEach(item => {
            flowObjectsToRemove = flowObjectsToRemove.concat(this.$selectedFlowObjects().filter(x => x.groupingColumnId == item.id));
            groupingColumnIndexesToRemove.push(item.outerIndex);
        });

        flowObjectsToRemove.forEach(item => flowObjectIndexesToRemove.push(item.outerIndex));

        flowObjectIndexesToRemove.sort((a, b) => b - a);
        flowObjectIndexesToRemove.forEach(item => Utils.updSignal(this.$selectedFlowObjects, x => {
            x.splice(item, 1);
            return x;
        }));
        this._selectedFlowObjectsChangeEvent.emit(this.$selectedFlowObjects());

        groupingColumnIndexesToRemove.sort((a, b) => b - a);
        groupingColumnIndexesToRemove.forEach(item => Utils.updSignal(this.$selectedGroupingColumns, x => {
            x.splice(item, 1);
            return x;
        }));
        this._selectedGroupingColumnsChangeEvent.emit(this.$selectedGroupingColumns());

        let clone = structuredClone(this.$selectedGroupings());
        clone.splice(groupingIndexToRemove, 1);
        this.$selectedGroupings.set(clone);
        this._selectedGroupingsChangeEvent.emit(this.$selectedGroupings());

        this.reorderFlowObjects();
        this.linkFlowObjects();
        event.stopPropagation();
    }

    /**
     * altera o tipo de uma Task situada na posição "GroupingColumn.InnerIndex == 1 && FlowObjectDefinition.InnerIndex == 0"
     * (e.g. caminho "Sim" de Tarefa de Avaliação)
     */
    changeTaskType(event: MouseEvent, flowObject: FlowObjectDefinition) {
        if (this.$isReadOnlyMode()) {
            event.stopPropagation();
            return;
        }

        flowObject.configSchema = JSON.stringify(new ConfigSchema());

        // circula entre os tipos para minimizar a complexidade da interface para oferecer ao usuário diferentes opções
        switch (flowObject.typeId) {
            case FlowObjectType.TaskForward:
                flowObject.typeId = FlowObjectType.TaskRegisterProcess;
                break;

            case FlowObjectType.TaskRegisterProcess:
                flowObject.typeId = FlowObjectType.Finish;
                break;

            case FlowObjectType.Finish:
                flowObject.typeId = FlowObjectType.FinishOutboundApi;
                break;

            case FlowObjectType.FinishOutboundApi:
                flowObject.typeId = FlowObjectType.FinishContent;
                break;

            default:
                flowObject.typeId = FlowObjectType.TaskForward;
                break;
        }

        flowObject.name = FlowObjectTypeDescription.get(+flowObject.typeId);
        flowObject.publicName = FlowObjectTypePublicName.get(+flowObject.typeId);

        event.stopPropagation();
    }

    getAvailableFlowObjectFeatureHint(flowObject: FlowObjectDefinition): string {
        switch (flowObject.typeId) {
            case FlowObjectType.StartForm:
                return 'availableFlowObjectStartForm';
            case FlowObjectType.StartInboundApi:
                return 'availableFlowObjectStartInboundApi';
            case FlowObjectType.TaskForward:
                return 'availableFlowObjectTaskForward';
            case FlowObjectType.TaskAcknowledge:
                return 'availableFlowObjectTaskAcknowledge';
            case FlowObjectType.TaskMidwayOutboundApi:
                return 'availableFlowObjectTaskMidwayOutboundApi';
            case FlowObjectType.TaskRegisterProcess:
                return 'availableFlowObjectTaskRegisterProcess';
            case FlowObjectType.TaskDispatchProcess:
                return 'availableFlowObjectTaskDispatchProcess';
            case FlowObjectType.TaskAddToProcess:
                return 'availableFlowObjectTaskAddToProcess';
            case FlowObjectType.GatewayApprove:
                return 'availableFlowObjectGatewayApprove';
            case FlowObjectType.GatewayRules:
                return 'availableFlowObjectGatewayRules';
            case FlowObjectType.GatewayPaths:
                return 'availableFlowObjectGatewayPaths';
            case FlowObjectType.Finish:
                return 'availableFlowObjectFinish';
            case FlowObjectType.FinishContent:
                return 'availableFlowObjectFinishContent';
            case FlowObjectType.FinishOutboundApi:
                return 'availableFlowObjectFinishOutboundApi';
            default:
                return null;
        }
    }

    onAfterSelectFlowObject(dto: OnAfterSelectFlowObjectDto) {
        this.reorderFlowObjects();

        // caso a Tarefa esteja sendo adicionada à Sequência Principal,
        // garante-se que seja posicionada antes da Task "GatewayPaths"
        if (dto.shouldMoveGroupingBackwards) {
            this.moveGroupingBackwards(null, dto.flowObject);
            return;
        }

        this.resolveNextFlowObjects();
        this.linkFlowObjects();
    }

    // #region [===== private methods =====]
    // #endregion

    private clearLinks() {
        document.querySelectorAll('.leader-line').forEach(elem => elem.parentNode.removeChild(elem));
    }

    private linkFlowObjects() {
        this.clearLinks();

        let interval = setInterval(() => {
            if (document.querySelector('.leader-line.in') != null) {
                clearInterval(interval);
                return;
            }

            this.drawLinks(this.$mainPathFlowObjects(), this.mainPathFlowObjectRefs, this.mainPathRef);

            if (this.$hasGatewayPath()) {
                this.drawLinks(this.$firstGatewayPathFlowObjects(), this.firstGatewayPathFlowObjectRefs, this.firstGatewayPathRef, true);
                this.drawLinks(this.$secondGatewayPathFlowObjects(), this.secondGatewayPathFlowObjectRefs, this.secondGatewayPathRef, true);
            }
        }, 100);
    }

    private drawLinks(flowObjects: FlowObjectDefinition[], flowObjectRefs: ElementRef<any>[], containerRef: ElementRef<any>, isGatewayPath = false) {
        Utils.flowAreaReady(() => {
            let currentOverlapIndex = 0;

            let options: any = {
                color: SCSS_VARS['$leader-line-color'],
                path: 'grid',
                startSocket: 'right',
                endSocket: 'left',
                dash: {
                    animation: true,
                    len: 50,
                    gap: 2
                }
            };

            // filtro para remover da lista de refs os elementos desconectados do DOM
            flowObjectRefs = flowObjectRefs.filter(x => document.body.contains(x.nativeElement));

            flowObjectRefs.forEach(el => {
                let elem = el.nativeElement;
                let lines = [];
                let currentFlowObject = flowObjects.find(item => item.id == elem.dataset.id);

                currentFlowObject?.next?.forEach(next => {
                    let endElement = flowObjectRefs.find(x => x.nativeElement.dataset.id == next.id)?.nativeElement;
                    if (endElement == null) return;

                    if ([
                        FlowObjectType.GatewayApprove,
                        FlowObjectType.GatewayRules,
                        FlowObjectType.GatewayPaths
                    ].includes(currentFlowObject.typeId)) {
                        let label = currentFlowObject.typeId == FlowObjectType.GatewayPaths ? '' : Enums.GatewayPathsLabels.False;
                        let offset = [-42, 0];

                        if (next.innerIndex == 0) {
                            label = currentFlowObject.typeId == FlowObjectType.GatewayPaths ? '' : Enums.GatewayPathsLabels.True;
                            offset = [-42, -33];
                        }

                        let captionLabelOptions = {
                            color: SCSS_VARS['$leader-line-labels-color'],
                            outlineColor: '',
                            fontSize: '1em',
                            fontWeight: 'bold',
                            offset: offset
                        };
                        let opt0 = JSON.parse(JSON.stringify(options));
                        opt0.endLabel = LeaderLine.captionLabel(label, captionLabelOptions);

                        lines.push(new LeaderLine(elem, endElement, opt0));
                    } else if (elem.offsetTop != endElement.offsetTop && Math.abs(endElement.offsetLeft - elem.offsetLeft) > 250) {
                        currentOverlapIndex++;
                        let overlapCaptionLabelOptions = {
                            color: '#fff',
                            outlineColor: '',
                            fontSize: '1.2em',
                            fontWeight: 'bold',
                            offset: [-5, -20]
                        };
                        let overlapOptions = {
                            plug: 'square',
                            plugSize: 2.5,
                            plugColor: '#ccc',
                            startLabel: LeaderLine.captionLabel(currentOverlapIndex.toString(), overlapCaptionLabelOptions),
                            endLabel: LeaderLine.captionLabel(currentOverlapIndex.toString(), overlapCaptionLabelOptions)
                        };

                        let endPoint = elem.offsetTop + (elem.offsetHeight / 2);
                        let opt1 = JSON.parse(JSON.stringify(options));
                        opt1.endPlug = overlapOptions.plug;
                        opt1.endPlugSize = overlapOptions.plugSize;
                        opt1.endPlugColor = overlapOptions.plugColor;
                        opt1.endLabel = overlapOptions.endLabel;
                        lines.push(new LeaderLine(elem, LeaderLine.pointAnchor(containerRef.nativeElement, { x: '100%', y: endPoint }), opt1));

                        let startPoint = endElement.offsetTop + (endElement.offsetHeight / 2);
                        let opt2 = JSON.parse(JSON.stringify(options));
                        opt2.startPlug = overlapOptions.plug;
                        opt2.startPlugSize = overlapOptions.plugSize;
                        opt2.startPlugColor = overlapOptions.plugColor;
                        opt2.startLabel = overlapOptions.startLabel;
                        lines.push(new LeaderLine(LeaderLine.pointAnchor(containerRef.nativeElement, { x: '0%', y: startPoint }), endElement, opt2));
                    } else {
                        lines.push(new LeaderLine(elem, endElement, options));
                    }
                });

                lines.forEach(line => {
                    line.position();
                    (document.querySelectorAll('.leader-line') as NodeListOf<HTMLElement>).forEach(elem => {
                        elem.dispatchEvent(new Event('focusin'));
                        elem.classList.add('in');
                        elem.dispatchEvent(new Event('focusout'));
                    });
                });
            });
        }, isGatewayPath);
    }

    private reorderFlowObjects() {
        // #region [Main Path]
        this.$mainPathGroupings().forEach((item, idx) => item.outerIndex = idx);

        let bufferGroupingColumns = [];
        this.$mainPathGroupings().forEach(item => {
            bufferGroupingColumns = bufferGroupingColumns.concat(this.$mainPathGroupingColumns().filter(x => x.groupingId == item.id));
        });
        bufferGroupingColumns.forEach((item, idx) => item.outerIndex = idx);

        let bufferFlowObjects = [];
        bufferGroupingColumns.forEach(item => {
            bufferFlowObjects = bufferFlowObjects.concat(this.$mainPathFlowObjects().filter(x => x.groupingColumnId == item.id));
        });
        bufferFlowObjects.forEach((item, idx) => item.outerIndex = idx);
        // #endregion

        // #region [First Gateway Path]
        this.$firstGatewayPathGroupings().forEach((item, idx) => item.outerIndex = this.$mainPathGroupings().length + idx);

        bufferGroupingColumns = [];
        this.$firstGatewayPathGroupings().forEach(item => {
            bufferGroupingColumns = bufferGroupingColumns.concat(this.$firstGatewayPathGroupingColumns().filter(x => x.groupingId == item.id));
        });
        bufferGroupingColumns.forEach((item, idx) => item.outerIndex = this.$mainPathGroupingColumns().length + idx);

        bufferFlowObjects = [];
        bufferGroupingColumns.forEach(item => {
            bufferFlowObjects = bufferFlowObjects.concat(this.$firstGatewayPathFlowObjects().filter(x => x.groupingColumnId == item.id));
        });
        bufferFlowObjects.forEach((item, idx) => item.outerIndex = this.$mainPathFlowObjects().length + idx);
        // #endregion

        // #region [Second Gateway Path]
        this.$secondGatewayPathGroupings().forEach((item, idx) => item.outerIndex = this.$mainPathGroupings().length + this.$firstGatewayPathGroupings().length + idx);

        bufferGroupingColumns = [];
        this.$secondGatewayPathGroupings().forEach(item => {
            bufferGroupingColumns = bufferGroupingColumns.concat(this.$secondGatewayPathGroupingColumns().filter(x => x.groupingId == item.id));
        });
        bufferGroupingColumns.forEach((item, idx) => item.outerIndex = this.$mainPathGroupingColumns().length + this.$firstGatewayPathGroupingColumns().length + idx);

        bufferFlowObjects = [];
        bufferGroupingColumns.forEach(item => {
            bufferFlowObjects = bufferFlowObjects.concat(this.$secondGatewayPathFlowObjects().filter(x => x.groupingColumnId == item.id));
        });
        bufferFlowObjects.forEach((item, idx) => item.outerIndex = this.$mainPathFlowObjects().length + this.$firstGatewayPathFlowObjects().length + idx);
        // #endregion

        this.$selectedFlowObjects.set(this.$selectedFlowObjects().toSorted((a, b) => a.outerIndex - b.outerIndex));
        this._selectedFlowObjectsChangeEvent.emit(this.$selectedFlowObjects());
        this.$selectedGroupingColumns.set(this.$selectedGroupingColumns().toSorted((a, b) => a.outerIndex - b.outerIndex));
        this._selectedGroupingColumnsChangeEvent.emit(this.$selectedGroupingColumns());
        this.$selectedGroupings.set(this.$selectedGroupings().toSorted((a, b) => a.outerIndex - b.outerIndex));
        this._selectedGroupingsChangeEvent.emit(this.$selectedGroupings());

        Utils.updSignal(this.$selectedGroupingColumns, x => {
            x.forEach(y => y.grouping = this.$selectedGroupings().find(z => z.id == y.groupingId));
            return x;
        });

        Utils.updSignal(this.$selectedFlowObjects, x => {
            x.forEach(y => y.groupingColumn = this.$selectedGroupingColumns().find(z => z.id == y.groupingColumnId));
            return x;
        });

        this.resolveNextFlowObjects();
    }

    private resolveNextFlowObjects() {
        this.$mainPathGroupingColumns().forEach((groupingColumn, idx) => {
            if (groupingColumn.flowObjectDefinitions.some(x => x.typeId == FlowObjectType.Dummy)) return;

            let nextToBe = [];
            if (idx < this.$mainPathGroupingColumns().length - 1) {
                nextToBe = this.$mainPathFlowObjects().filter(x => this.$mainPathGroupingColumns()[idx + 1].flowObjectDefinitions.some(y => y.id == x.id));
            }

            this.$mainPathFlowObjects().find(x => x.id == groupingColumn.flowObjectDefinitions.find(z => z.innerIndex == 0).id).next = nextToBe;
        });

        if (!this.$hasGatewayPath()) return;

        this.$firstGatewayPathGroupingColumns().forEach((groupingColumn, idx) => {
            let nextToBe = [];
            if (idx < this.$firstGatewayPathGroupingColumns().length - 1) {
                nextToBe = this.$firstGatewayPathFlowObjects().filter(x => this.$firstGatewayPathGroupingColumns()[idx + 1].flowObjectDefinitions.some(y => y.id == x.id));
            }

            this.$firstGatewayPathFlowObjects().find(x => x.id == groupingColumn.flowObjectDefinitions.find(z => z.innerIndex == 0).id).next = nextToBe;
        });

        this.$secondGatewayPathGroupingColumns().forEach((groupingColumn, idx) => {
            let nextToBe = [];
            if (idx < this.$secondGatewayPathGroupingColumns().length - 1) {
                nextToBe = this.$secondGatewayPathFlowObjects().filter(x => this.$secondGatewayPathGroupingColumns()[idx + 1].flowObjectDefinitions.some(y => y.id == x.id));
            }

            this.$secondGatewayPathFlowObjects().find(x => x.id == groupingColumn.flowObjectDefinitions.find(z => z.innerIndex == 0).id).next = nextToBe;
        });
    }
}
